import {useCallback, useEffect, useRef} from 'react';

import {useAppDispatch, useAppSelector} from 'stores/redux_store';

import {asyncResult} from 'utils/async_result';

import {isDesktopApp} from 'utils/user_agent';

import {getPushConfig, updatePWASession} from './api/push_api';
import {setSubscription, removeSubscription} from './slices/push_subscription_slice';
import {useNotificationPermission} from './use_notification_permission';

const canSubscribe = 'Notification' in window && 'serviceWorker' in navigator && !isDesktopApp();

export function usePushSubscription() {
    const dispatch = useAppDispatch();
    const {requestPermission} = useNotificationPermission();
    const subscribed = useAppSelector((state) => state.pushSubscription.hasSubscription);
    const shouldCheckSubscription = useRef(subscribed);

    useEffect(() => {
        const run = async () => {
            if (!shouldCheckSubscription.current) {
                return;
            }
            try {
                const registration = await navigator.serviceWorker.getRegistration();
                const subscription = await registration!.pushManager.getSubscription();
                if (!subscription) {
                    dispatch(removeSubscription());
                }
            } catch (e) {
                dispatch(removeSubscription());
            }
        };
        run();
    }, [dispatch]);

    const subscribe = useCallback(() => asyncResult(async () => {
        if (!canSubscribe) {
            throw Error('Push API is not available');
        }

        const [permission, error] = await requestPermission();

        if (error) {
            throw error;
        }

        if (permission !== 'granted') {
            throw new Error('Notification permission denied');
        }

        const registration = await navigator.serviceWorker.getRegistration();

        if (!registration) {
            throw new Error('Service worker is not registered');
        }

        let subscription = await registration.pushManager.getSubscription();

        if (!subscription) {
            const config = await getPushConfig();

            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlB64ToUint8Array(config.VapidPublicKey),
            });
        }

        if (!subscription) {
            throw new Error('Push subscription is not created');
        }

        await updatePWASession(subscription);
        dispatch(setSubscription());
        return subscription;
    }), [requestPermission, dispatch]);

    return {
        subscribe,
        canSubscribe,
        subscribed,
    };
}

function urlB64ToUint8Array(data: string): Uint8Array {
    const base64 = data.replace(/-/g, '+').replace(/_/g, '/');

    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const base64WithPadding = base64 + padding;

    const binaryString = atob(base64WithPadding);
    const byteArray = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
        byteArray[i] = binaryString.charCodeAt(i);
    }

    return byteArray;
}
